import type { OrderQuotaConsumer } from "~/modules/quota/api/quota/quotaTypes.ts";
import { formatDateToYYYYMMDD } from "~/utils/dateAndTimeUtils.ts";

// A quota has ended when its usable period ends before today. A null end is open.
export function quotaUsablePeriodHasEnded(quota: OrderQuotaConsumer["quota"]): boolean {
	return quota.usableUntil !== null && quota.usableUntil.slice(0, 10) < formatDateToYYYYMMDD(new Date());
}

// A consumer takes bookings when its quota is bookable and has not ended, and its own booking toggle is
// on. The time tracking form applies the same check for the tracking day (quotaTakesBookingOnDay in
// timeTrackingUtils.ts). An ended quota still takes back-dated trackings there.
export function quotaConsumerTakesBookings(consumer: OrderQuotaConsumer): boolean {
	return consumer.quota.isBookable && consumer.timeTrackingEnabled && !quotaUsablePeriodHasEnded(consumer.quota);
}

// Two usable periods overlap when they share at least one day. A null bound is open.
function usablePeriodsOverlap(a: OrderQuotaConsumer["quota"], b: OrderQuotaConsumer["quota"]): boolean {
	const aFrom = a.usableFrom?.slice(0, 10);
	const aUntil = a.usableUntil?.slice(0, 10);
	const bFrom = b.usableFrom?.slice(0, 10);
	const bUntil = b.usableUntil?.slice(0, 10);

	const aStartsAfterB = aFrom !== undefined && bUntil !== undefined && aFrom > bUntil;
	const bStartsAfterA = bFrom !== undefined && aUntil !== undefined && bFrom > aUntil;

	return !aStartsAfterB && !bStartsAfterA;
}

// The consumers of one order whose quotas can take the same tracking. For a tracking in the overlap the
// time tracking form disables the quota switch, so the order needs a fix on the quota details page.
// Ended quotas are skipped, because their overlap affects back-dated trackings only.
export function getOverlappingBookableQuotaConsumers(consumers: OrderQuotaConsumer[]): OrderQuotaConsumer[] {
	const bookableConsumers = consumers.filter(quotaConsumerTakesBookings);

	return bookableConsumers.filter((consumer) => bookableConsumers.some((other) => other.id !== consumer.id
		&& usablePeriodsOverlap(consumer.quota, other.quota)));
}
